import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ShoppingBag, ArrowLeft, Calendar, Clock, User } from 'lucide-react';

const blogPosts = [
  {
    id: 'louis-vuitton-wertanlage',
    title: 'Warum eine Louis Vuitton Tasche mehr als nur ein Accessoire ist – Luxus als Wertanlage',
    excerpt: 'Luxusmode wird oft als reiner Konsum betrachtet. Doch bestimmte Marken und Modelle haben sich über die Jahre als erstaunlich wertstabil erwiesen.',
    date: '2025-01-15',
    readTime: '8 min',
    author: 'Love Luxe',
    image: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=800&q=80',
    content: `
## Luxusmode wird oft als reiner Konsum betrachtet. Doch bestimmte Marken und Modelle haben sich über die Jahre als erstaunlich wertstabil erwiesen.

Besonders Taschen von **Louis Vuitton** gelten nicht nur als stilvolle Begleiter im Alltag, sondern zunehmend auch als **potenzielle Wertanlage**. Wer eine solche Tasche kauft, investiert häufig nicht nur in Design und Handwerkskunst, sondern auch in ein Produkt, dessen Wert über Jahre stabil bleiben oder sogar steigen kann.

## 1. Eine der stärksten Luxusmarken der Welt

Louis Vuitton gehört zu den bekanntesten Luxusmarken weltweit und ist Teil des LVMH-Konzerns. Die Marke steht seit über 160 Jahren für Qualität, Prestige und zeitloses Design. Diese starke Markenposition sorgt dafür, dass Produkte von Louis Vuitton auch auf dem **Secondhand- und Vintage-Markt sehr gefragt** sind.

Während viele Konsumgüter nach dem Kauf sofort an Wert verlieren, ist das bei Luxusartikeln dieser Kategorie oft anders. Beliebte Modelle bleiben über Jahrzehnte hinweg gefragt, was sich direkt auf ihre **Wiederverkaufspreise** auswirkt.

## 2. Regelmäßige Preiserhöhungen steigern den Wert

Ein entscheidender Faktor für den Wert einer Louis Vuitton Tasche sind die **regelmäßigen Preisanpassungen** der Marke. Luxusmarken erhöhen ihre Preise häufig jährlich oder mehrmals pro Jahr.

Das bedeutet: Wer eine Tasche heute kauft, besitzt morgen ein Produkt, das im Laden bereits deutlich teurer sein kann. Dadurch steigt auch der Preis auf dem Zweitmarkt.

**Ein Beispiel aus der Praxis:** Viele klassische Louis Vuitton Modelle haben sich innerhalb von zehn Jahren im Preis **nahezu verdoppelt**. Besitzer profitieren dadurch indirekt von der Preisentwicklung der Marke.

## 3. Zeitlose Designs statt kurzlebiger Trends

Ein weiterer Grund, warum Louis Vuitton Taschen ihren Wert behalten, ist ihr **zeitloses Design**. Klassiker wie das Monogram Canvas oder ikonische Modelle werden über viele Jahre hinweg produziert und bleiben modisch relevant.

Zeitlose Produkte haben einen entscheidenden Vorteil: Sie sind nicht an eine bestimmte Saison gebunden und verlieren daher weniger schnell an Attraktivität.

Dadurch bleibt auch die Nachfrage auf dem Gebrauchtmarkt konstant hoch.

## 4. Hohe Nachfrage auf dem Secondhand-Markt

Der Markt für **Pre-Loved Luxury** wächst weltweit stark. Plattformen für Secondhand-Luxus verzeichnen jährlich steigende Umsätze.

Viele Käufer entscheiden sich bewusst für gebrauchte Designerstücke, weil sie:

- günstiger sind als neu
- nachhaltiger sind
- teilweise schwer erhältliche Modelle anbieten

Louis Vuitton Taschen gehören dabei zu den **meistgesuchten Artikeln**. Besonders gut erhaltene Modelle können oft für einen großen Teil ihres ursprünglichen Kaufpreises weiterverkauft werden.

## 5. Qualität und Langlebigkeit

Ein weiterer Grund für die Wertstabilität liegt in der **Verarbeitungsqualität**. Louis Vuitton ist bekannt für robuste Materialien wie beschichtetes Canvas, hochwertige Lederarten und präzise Handarbeit.

Viele Taschen halten bei guter Pflege **über Jahrzehnte**, ohne stark an Funktion oder Optik zu verlieren. Diese Langlebigkeit macht sie besonders attraktiv für Käufer auf dem Zweitmarkt.

## 6. Limitierte Editionen und begehrte Modelle

Neben klassischen Taschen gibt es auch limitierte Kollektionen oder Kooperationen mit Künstlern und Designern. Solche Modelle können im Laufe der Zeit besonders begehrt werden.

Limitierte Stücke haben einen entscheidenden Vorteil: Da ihre Verfügbarkeit begrenzt ist, kann ihre Nachfrage später deutlich steigen. In manchen Fällen werden solche Taschen sogar **über dem ursprünglichen Verkaufspreis gehandelt**.

## Fazit: Luxus mit Potenzial

Eine Louis Vuitton Tasche ist zweifellos ein hochwertiges Modeaccessoire – doch sie kann auch mehr sein als das. Durch starke Markenwerte, kontinuierliche Preiserhöhungen, hohe Nachfrage und langlebige Qualität besitzen viele Modelle ein erstaunliches Maß an Wertstabilität.

Natürlich sollte man Luxusmode nicht ausschließlich als Finanzinvestition betrachten. Doch im Vergleich zu vielen anderen Konsumgütern bietet eine Louis Vuitton Tasche einen klaren Vorteil:

**Man kann sie nutzen, genießen und gleichzeitig einen Teil des investierten Geldes langfristig erhalten.**

Damit vereint sie zwei Welten – **Lifestyle und Wertanlage**.
    `
  }
];

const BlogPage = () => {
  const navigate = useNavigate();
  const [selectedPost, setSelectedPost] = React.useState(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-CH', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const renderContent = (content) => {
    const lines = content.trim().split('\n');
    return lines.map((line, index) => {
      // Headers
      if (line.startsWith('## ')) {
        return (
          <h2 key={index} className="text-2xl font-semibold mt-8 mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}>
            {line.replace('## ', '')}
          </h2>
        );
      }
      // Bold text handling
      if (line.startsWith('**') && line.endsWith('**')) {
        return (
          <p key={index} className="text-lg font-semibold my-4" style={{ color: 'var(--charcoal)' }}>
            {line.replace(/\*\*/g, '')}
          </p>
        );
      }
      // List items
      if (line.startsWith('- ')) {
        return (
          <li key={index} className="ml-6 my-2" style={{ color: 'var(--warm-gray)' }}>
            {line.replace('- ', '')}
          </li>
        );
      }
      // Empty lines
      if (line.trim() === '') {
        return <br key={index} />;
      }
      // Regular paragraphs with bold text
      const parts = line.split(/(\*\*.*?\*\*)/g);
      return (
        <p key={index} className="my-4 leading-relaxed" style={{ color: 'var(--warm-gray)' }}>
          {parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={i} style={{ color: 'var(--charcoal)' }}>{part.replace(/\*\*/g, '')}</strong>;
            }
            return part;
          })}
        </p>
      );
    });
  };

  if (selectedPost) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--cream-white)' }}>
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <ShoppingBag className="w-8 h-8" style={{ color: 'var(--lv-brown)' }} />
              <div>
                <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}>
                  Love Luxe
                </h1>
                <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--warm-gray)', letterSpacing: '1.5px' }}>
                  Second-Hand Luxury
                </p>
              </div>
            </div>
            <Button 
              variant="outline"
              onClick={() => setSelectedPost(null)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zum Blog
            </Button>
          </nav>
        </header>

        {/* Article */}
        <article className="max-w-3xl mx-auto px-6 py-12">
          <img 
            src={selectedPost.image} 
            alt={selectedPost.title}
            className="w-full h-64 md:h-96 object-cover rounded-lg shadow-lg mb-8"
          />
          
          <div className="flex items-center gap-4 mb-6 text-sm" style={{ color: 'var(--warm-gray)' }}>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(selectedPost.date)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {selectedPost.readTime} Lesezeit
            </span>
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {selectedPost.author}
            </span>
          </div>

          <h1 
            className="text-3xl md:text-4xl font-light mb-8 leading-tight"
            style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}
          >
            {selectedPost.title}
          </h1>

          <div className="prose max-w-none">
            {renderContent(selectedPost.content)}
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 rounded-lg text-center" style={{ backgroundColor: 'var(--soft-gray)' }}>
            <h3 className="text-2xl font-light mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}>
              Entdecken Sie unsere Kollektion
            </h3>
            <p className="mb-6" style={{ color: 'var(--warm-gray)' }}>
              Finden Sie Ihre perfekte Louis Vuitton Tasche – authentifiziert und zu fairen Preisen.
            </p>
            <Button 
              onClick={() => navigate('/collection')}
              style={{ backgroundColor: 'var(--lv-brown)', color: 'white' }}
            >
              Zur Kollektion
            </Button>
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--cream-white)' }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            <ShoppingBag className="w-8 h-8" style={{ color: 'var(--lv-brown)' }} />
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}>
                Love Luxe
              </h1>
              <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--warm-gray)', letterSpacing: '1.5px' }}>
                Second-Hand Luxury
              </p>
            </div>
          </div>
          <Button 
            variant="outline"
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </Button>
        </nav>
      </header>

      {/* Blog Header */}
      <section className="py-16" style={{ backgroundColor: 'var(--soft-gray)' }}>
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 
            className="text-4xl md:text-5xl font-light mb-4"
            style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}
          >
            Blog
          </h1>
          <p style={{ color: 'var(--warm-gray)' }}>
            Insights, Tipps und Neuigkeiten aus der Welt des Luxus
          </p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <Card 
                key={post.id}
                className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => setSelectedPost(post)}
              >
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-48 object-cover"
                />
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-3 text-xs" style={{ color: 'var(--warm-gray)' }}>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(post.date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </span>
                  </div>
                  <h2 
                    className="text-xl font-medium mb-3 line-clamp-2"
                    style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}
                  >
                    {post.title}
                  </h2>
                  <p className="text-sm line-clamp-3" style={{ color: 'var(--warm-gray)' }}>
                    {post.excerpt}
                  </p>
                  <Button 
                    variant="link" 
                    className="mt-4 p-0"
                    style={{ color: 'var(--lv-brown)' }}
                  >
                    Weiterlesen →
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
